import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import {
  getAutomations,
  getAutomationsForRecordGroup,
  getAutomation,
  createAutomation,
  updateAutomation,
  enableAutomation,
  disableAutomation,
  deleteAutomation,
  getTrashedAutomations,
  getAutomationTrashStats,
  restoreAutomation,
  permanentlyDeleteAutomation,
} from '@/services/api/automation.service'
import type { UpdateAutomationRequest } from '@/types/api/automation'

import { useWorkspace } from './useWorkspace'

export function useAutomations() {
  const { currentWorkspaceId } = useWorkspace()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'automations'],
    queryFn: getAutomations,
    enabled: !!currentWorkspaceId,
    staleTime: 60 * 1000,
  })

  const createMutation = useMutation({
    mutationFn: createAutomation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'automations'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAutomationRequest }) =>
      updateAutomation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'automations'] })
    },
  })

  const toggleMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      if (enabled) {
        await enableAutomation(id)
      } else {
        await disableAutomation(id)
      }
    },
    onMutate: async ({ id, enabled }) => {
      // Cancel any in-flight refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['workspace', currentWorkspaceId, 'automations'] })
      await queryClient.cancelQueries({ queryKey: ['automations', id] })

      // Snapshot previous values for rollback
      const previousList = queryClient.getQueryData<import('@/types/api/automation').Automation[]>(
        ['workspace', currentWorkspaceId, 'automations']
      )
      const previousDetail = queryClient.getQueryData<import('@/types/api/automation').Automation>(
        ['automations', id]
      )

      // Optimistically update the list cache
      queryClient.setQueryData<import('@/types/api/automation').Automation[]>(
        ['workspace', currentWorkspaceId, 'automations'],
        (old) => old?.map((a) => (a.id === id ? { ...a, enabled } : a)) ?? []
      )

      // Optimistically update the detail cache
      queryClient.setQueryData<import('@/types/api/automation').Automation>(
        ['automations', id],
        (old) => (old ? { ...old, enabled } : old)
      )

      return { previousList, previousDetail, id }
    },
    onError: (_err, _vars, context) => {
      // Roll back both caches
      if (context?.previousList !== undefined) {
        queryClient.setQueryData(
          ['workspace', currentWorkspaceId, 'automations'],
          context.previousList
        )
      }
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(
          ['automations', context.id],
          context.previousDetail
        )
      }
    },
    onSettled: (_data, _err, { id }) => {
      // Always sync from server after mutation settles
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'automations'] })
      queryClient.invalidateQueries({ queryKey: ['automations', id] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteAutomation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'automations'] })
    },
  })

  return {
    automations: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    create: createMutation.mutate,
    update: updateMutation.mutate,
    toggle: toggleMutation.mutate,
    delete: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isToggling: toggleMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}

/** @param options.enabled set false to skip loading (e.g. the user may not view automations) */
export function useAutomationsForRecordGroup(recordGroupId: string, options: { enabled?: boolean } = {}) {
  const { currentWorkspaceId } = useWorkspace()

  return useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'automations', 'record-group', recordGroupId],
    queryFn: () => getAutomationsForRecordGroup(recordGroupId),
    enabled: !!recordGroupId && !!currentWorkspaceId && options.enabled !== false,
    staleTime: 60 * 1000,
  })
}

export function useAutomationDetail(automationId: string) {
  const isNew = automationId === 'new'
  const query = useQuery({
    queryKey: ['automations', automationId],
    queryFn: () => getAutomation(automationId),
    enabled: !!automationId && !isNew,
  })

  return {
    automation: query.data,
    isLoading: query.isLoading,
    error: query.error,
  }
}

export function useAutomationTrash(pageNumber: number, pageSize: number) {
  const { currentWorkspaceId } = useWorkspace()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'automations-trash', pageNumber, pageSize],
    queryFn: () => getTrashedAutomations(pageNumber, pageSize),
    enabled: !!currentWorkspaceId,
    staleTime: 30 * 1000,
  })

  const statsQuery = useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'automations-trash-stats'],
    queryFn: getAutomationTrashStats,
    enabled: !!currentWorkspaceId,
    staleTime: 60 * 1000,
  })

  const restoreMutation = useMutation({
    mutationFn: restoreAutomation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'automations-trash'],
      })
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'automations-trash-stats'],
      })
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'automations'],
      })
    },
  })

  const permanentlyDeleteMutation = useMutation({
    mutationFn: permanentlyDeleteAutomation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'automations-trash'],
      })
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'automations-trash-stats'],
      })
    },
  })

  return {
    trashedAutomations: query.data?.content ?? [],
    pagination: {
      totalElements: query.data?.totalElements ?? 0,
      totalPages: query.data?.totalPages ?? 0,
      currentPage: query.data?.number ?? 0,
      pageSize: query.data?.size ?? 10,
      isFirst: query.data?.first ?? true,
      isLast: query.data?.last ?? true,
      isEmpty: query.data?.empty ?? true,
    },
    isLoading: query.isLoading,
    error: query.error,
    stats: statsQuery.data,
    statsIsLoading: statsQuery.isLoading,
    restore: restoreMutation.mutate,
    permanentlyDelete: permanentlyDeleteMutation.mutate,
    isRestoring: restoreMutation.isPending,
    isPermanentlyDeleting: permanentlyDeleteMutation.isPending,
    restoreError: restoreMutation.error,
    permanentlyDeleteError: permanentlyDeleteMutation.error,
  }
}
